import { ColumnStateType } from '@ant-design/pro-table/es/typing';
import { RequestData } from '@ant-design/pro-table/lib';
import { ProColumns } from '@ant-design/pro-table';
import { SysDict, SysOrgNode, SysRegionNode, UmsOrgKindNode } from '@/services/admin/AdminTypings';
import {
  dataId,
  DictScene,
  PermissionsType,
  PermissionType,
  RolesType,
  RoleType,
} from '@/utils/types/common';
import React, { useEffect, useState } from 'react';
import {
  dictInfo,
  queryOrgKinds,
  queryRegions,
  querySysOrg,
  querySysOrgChild,
  querySysOrgDepartment,
} from '@/services/CommonService';
import type { ColumnsState } from '@ant-design/pro-table/es/Store/Provide';
import { useAccess } from '@@/exports';
import { Key } from 'antd/es/table/interface';
import { FileViewer } from '@/components/Viewer/FileViewer';
import Env from '@/conf/env';
import { API_PREFIX } from '@/conf/constants';
import { Dayjs } from 'dayjs';
import { DatePickerProps } from 'antd';
import { useRequest } from 'ahooks';
import { v4 as uv4, v5 as uv5, v6 as uv6 } from 'uuid';

const UUID_NAMESPACE = '72f018b0-e978-4403-8cf9-026a497bf824';

export const BASE_URL = `${Env.baseURL}${API_PREFIX}`;
export const BASE_IMG =
  "data:image/svg+xml,%3Csvg class='icon' viewBox='0 0 1024 1024' xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cpath d='M89.043 0h845.914Q1024 0 1024 89.043v845.914q0 89.043-89.043 89.043H89.043Q0 1024 0 934.957V89.043Q0 0 89.043 0z' fill='%23EEE'/%3E%3Cpath d='M333.913 222.609a111.304 111.304 0 1 1-222.609 0 111.304 111.304 0 0 1 222.609 0z' fill='%23D9D9D9'/%3E%3Cpath d='M300.522 478.609L0 701.217v211.479a89.043 89.043 0 0 0 89.043 89.043h912.696L456.348 601.043 300.522 478.61z' fill='%23C2C2C2'/%3E%3Cpath d='M1024 523.13L834.783 333.913 0 912.696v22.26A89.043 89.043 0 0 0 89.043 1024h868.174A66.783 66.783 0 0 0 1024 957.217V523.13z' fill='%23D9D9D9'/%3E%3C/svg%3E";

// sysOrgDevelopment

export declare type RequestResult<T = any> = {
  code: number;
  message?: string;
  timestamp?: number;
  data: T;
  _R_?: boolean;
};

export declare type PagerResult<DataSource extends Record<string, any>> = {
  current?: number;
  pageSize?: number;
  pageTotal?: number;
  success?: boolean;
  total?: number;
  data: DataSource[];
  ext?: any;
};

export const uuidv4 = (): string => {
  return uv4();
};

export const uuidv5 = (str: string): string => {
  return uv5(str, UUID_NAMESPACE);
};

export const uuidv6 = (): string => {
  return uv6();
};

export const uuid = (): string => {
  return uv4().replace(/-/g, '');
};

export const getIndexData = <DataSource extends Record<string, any>>(
  result: PagerResult<DataSource>,
): Partial<RequestData<DataSource>> => {
  return {
    success: result?.success,
    total: result?.total || 0,
    data: result?.data || [],
    ext: result?.ext,
  };
};

export const fetchIndexData = async <DataSource extends Record<string, any>>(
  handle: () => Promise<any>,
): Promise<Partial<RequestData<DataSource>>> => {
  const response = await handle();
  return getIndexData<DataSource>(response as PagerResult<DataSource>);
};

export const getAppName = () => {
  return SUP_APP ?? 'admin';
};

export const getId = (id: Key | null | undefined): number => {
  if (!id) {
    return 0;
  }
  const val = Number(id);
  if (isNaN(val) || val < 1) {
    return 0;
  }
  return val;
};

export const sizeToK = (size: number) => {
  if (!size || size < 1) {
    return 0;
  }
  return size / 1024;
};

export const sizeToM = (size: number) => {
  if (!size || size < 1) {
    return 0;
  }
  return size / 1024 / 1024;
};

export const toFloat = (value: any) => {
  return parseFloat(value);
};

export const toInt = (value: any) => {
  return parseInt(value);
};

export const isNumber = (value: any) => {
  return !isNaN(toFloat(value));
};

export const isID = (value: any) => {
  const val = toInt(value);
  if (isNaN(val)) {
    return false;
  }
  return val > 0;
};

export const getPermission = (
  key: string | undefined,
  permissions: PermissionsType,
): PermissionType => {
  if (!key || !permissions) {
    return undefined;
  }
  return permissions[key] || [];
};

export const getRole = (key: string | undefined, roles: RolesType): RoleType => {
  if (!key || !roles) {
    return undefined;
  }
  return roles[key] || [];
};

export const hasPermission = (permissions: PermissionType, access?: Object): boolean => {
  if ((permissions || []).length < 1) return true;
  if (typeof access === undefined || access === undefined) {
    access = useAccess();
  }
  // if(access){
  //   return true;
  // }
  if (Object.keys(access || {}).length < 1) return false;
  for (const permissionsKey of permissions || []) {
    if (access[permissionsKey.trim()] || access[permissionsKey.trim().toLowerCase()]) {
      return true;
    }
  }
  return false;
};

export const hasRole = (roles: PermissionType, access?: Object): boolean => {
  if ((roles || []).length < 1) return true;
  if (typeof access === undefined || access === undefined) {
    access = useAccess();
  }
  // if(access){
  //   return true;
  // }
  const accessRoles = (access || {})?.['roles'] || [];
  if ((accessRoles || []).length < 1) return false;
  for (const rolesKey of roles || []) {
    if (accessRoles.includes(rolesKey)) {
      return true;
    }
  }
  return false;
};

export const accessAble = (
  permissions: PermissionType,
  roles: PermissionType,
  access?: Object,
): boolean => {
  if ((permissions || []).length < 1 && (roles || []).length < 1) return true;
  if (typeof access === undefined || access === undefined) {
    access = useAccess();
  }
  if (Object.keys(access || {}).length < 1) return false;
  return hasPermission(permissions, access) && hasRole(roles, access);
};

export const flatten = (data: any[], res: any[], childrenKey = 'children') => {
  data.forEach((item) => {
    res.push(item);
    if (item[childrenKey] && (item[childrenKey] || []).length > 0) {
      flatten(item[childrenKey], res, childrenKey);
    }
  });
  return res;
};

export const isReactElement = (node: React.ReactNode): boolean => {
  return !(
    !node ||
    typeof node == 'string' ||
    typeof node == 'number' ||
    typeof node == 'bigint' ||
    typeof node == 'boolean' ||
    typeof node[Symbol.iterator] === 'function' ||
    typeof node == 'undefined'
  );
};

export const urlPath = (path: string) => {
  return `${BASE_URL}${path}`;
};

export const uploadUrl = (isPublic?: boolean) => {
  if (isPublic) {
    return urlPath('/sys/file/upload/public');
  }
  return urlPath('/sys/file/upload');
};

export const fileUrl = (fileId: dataId) => {
  return urlPath(`/sys/file/response/${fileId}`);
};

export const dictValue = (value: string | undefined) => {
  if ((value || '') == '') {
    return '';
  }
  const arr = (value || '').split('::');
  return arr[0].trim();
};

export const getObjectValue = (obj: any, key: string) => {
  if (
    !obj ||
    !('value' in obj) ||
    !obj.value ||
    !(key in obj.value) ||
    `${obj.value[key]}`.trim() == ''
  ) {
    return '-';
  }
  return obj.value[key];
};

export const stringBooleanObjectValue = (
  obj: any,
  key: string,
  tStr: string = '是',
  fStr: string = '否',
) => {
  const value = getObjectValue(obj, key);
  if (value == 'true') {
    return tStr;
  }
  return fStr;
};

export const moneyValue = (value: string | number | undefined | null) => {
  if (!value) {
    return '¥ 0.00 元';
  }
  // @ts-ignore
  const val = parseFloat(value);
  if (isNaN(val)) {
    return '¥ 0.00 元';
  }
  return `¥ ${val.toFixed(2)} 元`;
};

export const getYearMonth = (date: Dayjs) => date.year() * 12 + date.month();

export const disabled6MonthsDate: DatePickerProps['disabledDate'] = (current, { from, type }) => {
  if (from) {
    const minDate = from.add(-5, 'months');
    const maxDate = from.add(5, 'months');

    switch (type) {
      case 'year':
        return current.year() < minDate.year() || current.year() > maxDate.year();

      default:
        return (
          getYearMonth(current) < getYearMonth(minDate) ||
          getYearMonth(current) > getYearMonth(maxDate)
        );
    }
  }

  return false;
};

export const numberToLetter = (num: number): string => {
  if (num < 1 || num > 26) {
    throw new Error('Number must be between 1 and 26');
  }
  return String.fromCharCode(64 + num).toUpperCase();
};

export namespace CommonUtils {
  export const tableState = (
    project: string,
    model: string,
    params: {
      defaultColumnState?: Record<string, ColumnsState>;
      defaultValue?: Record<string, ColumnsState>;
    } & ColumnStateType = {},
    type: 'localStorage' | 'sessionStorage' = 'localStorage',
  ): ColumnStateType | undefined => {
    const { defaultColumnState, defaultValue, ...rest } = params || {};
    return {
      persistenceKey: `Table-${project}-${model}`,
      persistenceType: type,
      ...{
        defaultValue: {
          ...{
            ...(defaultValue || {}),
            ...(defaultColumnState || {}),
          },
          option: { fixed: 'right', disable: true },
        },
        ...(rest || {}),
      },
    };
  };

  export const useDict = (scene: DictScene, code: string) => {
    const [dict, setDict] = useState<SysDict | undefined>();
    const [children, setChildren] = useState<SysDict[] | undefined>();
    const queryDict = async () => {
      const dict = await dictInfo(scene, code);
      setDict(dict);
      setChildren(dict != null && 'children' in dict && dict.children ? dict.children : []);
    };
    useEffect(() => {
      queryDict();
    }, [scene, code]);

    return {
      dict,
      children,
    };
  };

  export const useRegion = (params?: { pkg?: string; manual?: boolean }) => {
    const [regions, setRegions] = useState<SysRegionNode[] | undefined>();
    const { pkg } = params || {};
    const getData = async () => {
      const values = await queryRegions((pkg || 'ZH').toUpperCase());
      setRegions(values);
    };
    useEffect(() => {
      getData();
    }, []);

    return {
      regions,
    };
  };

  export const useOrgKind = (params?: { manual?: boolean }) => {
    const [orgKinds, setOrgKinds] = useState<UmsOrgKindNode[] | undefined>();
    const queryData = async () => {
      const values = await queryOrgKinds();
      setOrgKinds(values);
    };
    useEffect(() => {
      queryData();
    }, []);

    return {
      orgKinds,
    };
  };

  export const useSysOrg = (params?: { manual?: boolean }) => {
    const [sysOrg, setSysOrg] = useState<SysOrgNode[] | undefined>();
    const queryData = async () => {
      const values = await querySysOrg();
      setSysOrg(values);
    };
    useEffect(() => {
      queryData();
    }, []);

    return {
      sysOrg,
    };
  };

  export const useSysOrgDepartment = (params?: { manual?: boolean }) => {
    const [sysOrgDepartment, setSysOrgDepartment] = useState<SysOrgNode[] | undefined>();
    const queryData = async () => {
      const values = await querySysOrgDepartment();
      setSysOrgDepartment(values);
    };
    useEffect(() => {
      queryData();
    }, []);

    return {
      sysOrgDepartment,
    };
  };

  export const useSysOrgChild = (params?: { manual?: boolean }) => {
    const [sysOrgChild, setSysOrgChild] = useState<SysOrgNode[] | undefined>();

    const { loading, run: refresh } = useRequest(querySysOrgChild, {
      // manual: true,
      debounceWait: 300,
      debounceLeading: true,
      debounceTrailing: false,
      onSuccess: (data: any) => {
        setSysOrgChild(data);
      },
    });

    // const queryData = async () => {
    //   const values = await querySysOrgChild();
    //   setSysOrgChild(values);
    // };
    // useEffect(() => {
    //   queryData();
    // }, []);

    return {
      refresh,
      loading,
      sysOrgChild,
    };
  };

  export const dictColumn = (
    column: Partial<ProColumns>,
    dicts?: SysDict[],
    scene?: DictScene,
    code?: string,
  ): ProColumns => {
    // boolean | ColumnFilterItem[];
    let enums = {};
    if ((dicts || []).length > 0) {
      (dicts || []).forEach((dict) => {
        enums[dict.dictValue || ''] = {
          text: dict.dictName,
        };
      });
    }
    return Object.assign(column, {
      filters: true,
      valueEnum: enums,
      valueType: 'select',
    });
  };

  export const dictColumns = (
    column: Partial<ProColumns>,
    dicts?: SysDict[],
    scene?: DictScene,
    code?: string,
  ): ProColumns => {
    // boolean | ColumnFilterItem[];
    let enums = {};
    if ((dicts || []).length > 0) {
      (dicts || []).forEach((dict) => {
        enums[dict.dictValue || ''] = {
          text: dict.dictName,
        };
      });
    }
    return Object.assign(column, {
      filters: false,
      valueEnum: enums,
      valueType: 'select',
    });
  };

  export const sensitiveColumn = (
    column: Partial<ProColumns>,
    type?: string,
    db?: boolean,
  ): ProColumns => {
    return column;
  };

  export const fileColumn = (
    column: Partial<ProColumns>,
    isImage?: boolean,
    multipleFile?: boolean,
  ): ProColumns => {
    return Object.assign(column, {
      render: (dom: React.ReactNode, row: any, index, action, schema) => {
        // console.log('dom:', dom);
        // console.log('row:', row);
        // console.log('index:', index);
        // console.log('action:', action);
        // console.log('schema:', schema);
        const value =
          row != null && column.dataIndex && column.dataIndex in row ? row[column.dataIndex] : '';
        // console.log('value:', value);
        // return area.join(' / ');
        return <FileViewer value={value} isImage={isImage} multiple={multipleFile} />;
      },
    });
  };

  export const columnValue = (columnText: string, valueIndex: number = 0) => {
    if ((columnText || '').trim().length < 1) {
      return [];
    }
    const text = columnText.split('@');
    let values: string[] = [];
    (text[0] || '')
      .trim()
      .split('||')
      .forEach((str) => {
        if ((str || '').trim().length < 1) {
          return;
        }
        const tmp = (str || '').trim().split('::');
        if (tmp[0].trim().length < valueIndex + 1) {
          return;
        }
        values.push(tmp[valueIndex].trim());
      });
    return values;
  };

  export const kindColumn = (column: Partial<ProColumns>): ProColumns => {
    return Object.assign(column, {
      render: (dom: React.ReactNode, row: any, index, action, schema) => {
        const area: string[] = columnValue(
          row != null && column.dataIndex in row && row[column.dataIndex]
            ? row[column.dataIndex]
            : '',
          0,
        );
        return area.join(' / ');
      },
    });
  };

  export const columnValueText = (value: string[], separator: string = '/'): string => {
    return value.join(separator);
  };

  export const areaColumn = (column: Partial<ProColumns>): ProColumns => {
    return Object.assign(column, {
      render: (dom: React.ReactNode, row: any, index, action, schema) => {
        const area: string[] = columnValue(
          row != null && schema.dataIndex in row && row[schema.dataIndex]
            ? row[schema.dataIndex]
            : '',
          0,
        );
        return area.join(' / ');
      },
    });
  };
}

export const tableState = CommonUtils.tableState;

export const dictColumn = CommonUtils.dictColumn;
export const dictColumns = CommonUtils.dictColumns;
export const sensitiveColumn = CommonUtils.sensitiveColumn;
export const fileColumn = CommonUtils.fileColumn;
export const columnValue = CommonUtils.columnValue;
export const kindColumn = CommonUtils.kindColumn;
export const columnValueText = CommonUtils.columnValueText;
export const areaColumn = CommonUtils.areaColumn;

export const useDict = CommonUtils.useDict;
export const useRegion = CommonUtils.useRegion;
export const useOrgKind = CommonUtils.useOrgKind;

export const useSysOrg = CommonUtils.useSysOrg;
export const useSysOrgDepartment = CommonUtils.useSysOrgDepartment;
export const useSysOrgChild = CommonUtils.useSysOrgChild;
